"use client";

import { useState, useTransition } from "react";
import { Sparkles } from "lucide-react";
import { toggleSteeze } from "@/app/actions/companion-people";

export type SteezeButtonState = {
  active: boolean;
  count: number;
};

type SteezeResult =
  | { ok: true; active: boolean }
  | { ok: false };

export function optimisticSteezeState(
  state: SteezeButtonState,
): SteezeButtonState {
  return {
    active: !state.active,
    count: Math.max(0, state.count + (state.active ? -1 : 1)),
  };
}

export function settleSteezeState(
  previous: SteezeButtonState,
  optimistic: SteezeButtonState,
  result: SteezeResult,
): SteezeButtonState {
  if (!result.ok) return previous;
  if (result.active === optimistic.active) return optimistic;
  return previous;
}

export function CompanionSteezeButton({
  squadId,
  momentId,
  initialCount,
  initialActive,
}: {
  squadId: string;
  momentId: string;
  initialCount: number;
  initialActive: boolean;
}) {
  const [state, setState] = useState<SteezeButtonState>({
    active: initialActive,
    count: Math.max(0, initialCount),
  });
  const [message, setMessage] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();

  function toggle() {
    const previous = state;
    const optimistic = optimisticSteezeState(previous);
    setState(optimistic);
    setMessage(null);
    startTransition(async () => {
      const result = await toggleSteeze({ squadId, momentId });
      setState(settleSteezeState(previous, optimistic, result));
      if (!result.ok) setMessage(result.error);
    });
  }

  return (
    <div className="mt-3">
      <button
        type="button"
        aria-pressed={state.active}
        disabled={pending}
        onClick={toggle}
        className={`inline-flex min-h-[44px] items-center gap-2 rounded-sm border px-3 text-[12px] font-semibold transition-colors disabled:cursor-wait disabled:opacity-60 ${state.active ? "border-teal bg-teal text-white" : "border-border bg-warm-bg text-navy hover:border-teal/50"}`}
      >
        <Sparkles className="h-4 w-4" aria-hidden="true" />
        <span>{state.active ? "Remove Steeze" : "Give Steeze"}</span>
        <span aria-label={`${state.count} Steeze`} className={`rounded-full px-2 py-0.5 tabular-nums ${state.active ? "bg-white/15" : "bg-white"}`}>
          {state.count}
        </span>
      </button>
      <p aria-live="polite" className="mt-1 min-h-4 text-[11px] text-terracotta">{message}</p>
    </div>
  );
}
